import {
    ComponentParams,
    ComponentRendering,
    Field,
    LinkField,
    useSitecoreContext,
} from "@sitecore-jss/sitecore-jss-nextjs";
import {
    AspectRatio,
    Box,
    Flex,
    RangeSlider,
    RangeSliderFilledTrack,
    RangeSliderThumb,
    RangeSliderTrack,
    IconButton,
} from "@chakra-ui/react";
import GenericHeader, { GenericHeaderProps } from "components/ui/GenericHeader";
import { useCallback, useEffect, useRef, useState } from "react";
import BundleIcon from "components/ui/BundleIcon";
import { transparentize } from "polished";
import Image from "next/image";
import SCHeading from "components/sc/SCHeading";
import SCText from "components/sc/SCText";
import SCLinkButton from "components/sc/SCLinkButton";
import useImageSizes from "hooks/useImageSizes";
import { useI18n } from "next-localization";
import { gtmCustomEvent } from "components/head/GTMTracking";

interface Fields extends GenericHeaderProps {
    Title: Field<string>;
    Text: Field<string>;
    CTA: LinkField;
    ThumbnailUrl: Field<string>;
    VideoUrl: Field<string>;
}

export type VideoProps = {
    rendering: ComponentRendering & { params: ComponentParams };
    params: { [key: string]: string };
    fields: Fields;
};

const Video = (props: VideoProps) => {
    const {
        sitecoreContext: { pageEditing },
    } = useSitecoreContext();

    const [videoElement, setVideoElement] = useState<HTMLVideoElement>();
    const [sliderValue, setSliderValue] = useState<number>(0);
    const [isVideoShown, setIsVideoShown] = useState<boolean>(false);
    const videoRef = useRef<HTMLVideoElement>(null);
    const { t } = useI18n();

    const hasVideo = props.fields?.VideoUrl?.value !== "";

    const sizes = useImageSizes({ base: 1 });

    const [lastLoggedPercentage, setLastLoggedPercentage] = useState<number>(0);

    const updateSliderDuration = () => {
        if (!videoElement) return;

        const progress = (videoElement.currentTime / videoElement.duration) * 100;
        setSliderValue(progress);

        // Determine the progress milestone and log if it's different from the last logged percentage
        let milestone: number | null = null;

        if (progress >= 25 && lastLoggedPercentage < 25) {
            milestone = 25;
        } else if (progress >= 50 && lastLoggedPercentage < 50) {
            milestone = 50;
        } else if (progress >= 75 && lastLoggedPercentage < 75) {
            milestone = 75;
        } else if (progress === 100 && lastLoggedPercentage < 100) {
            milestone = 100;
        }

        if (milestone !== null) {
            gtmCustomEvent("custom_event", {
                type: "video",
                sub_type: "video progress",
                value: `${milestone}%`,
                name: props.fields?.VideoUrl.value,
            });
            setLastLoggedPercentage(milestone);
        }
    };

    const videoEnded = () => {
        if (videoElement) {
            videoElement.pause();
            handleSliderChange(0);
        }
    };

    const playPauseMedia = useCallback(() => {
        if (videoElement?.paused) {
            videoElement.play();

            gtmCustomEvent("custom_event", {
                type: "video",
                sub_type: "video play",
                name: props.fields?.VideoUrl.value,
            });
        } else {
            videoElement?.pause();
        }
    }, [props.fields?.VideoUrl.value, videoElement]);

    const onVideoLoad = () => {
        if (videoElement) {
            setSliderValue(0);
            playPauseMedia();
        }
    };

    const handleSliderChange = useCallback(
        (value: number) => {
            if (videoElement) {
                const newProgress = (value / 100) * videoElement.duration;
                setSliderValue(0);
                videoElement.currentTime = newProgress;
            }
        },
        [videoElement]
    );

    const closePlayer = useCallback(() => {
        if (videoElement) {
            videoElement.pause();
            handleSliderChange(0);
            setIsVideoShown(false);
        }
    }, [handleSliderChange, videoElement]); // Add an empty array as the second argument to useCallback

    useEffect(() => {
        const handleKeypress = (e: KeyboardEvent) => {
            if (e.code === "Escape") {
                e.preventDefault();
                closePlayer();
            }

            if (e.code === "Space") {
                e.preventDefault();
                playPauseMedia();
            }
        };
        document.addEventListener("keydown", handleKeypress);

        return () => {
            document.removeEventListener("keydown", handleKeypress);
        };
    }, [closePlayer, playPauseMedia, videoElement]);

    useEffect(() => {
        if (isVideoShown) {
            if (!videoRef.current) return;
            setVideoElement(videoRef.current);
        }
    }, [isVideoShown]);

    return (
        <GenericHeader
            Id={props.rendering?.uid}
            HeaderHeadline={props.fields?.HeaderHeadline}
            HeaderSubHeadline={props.fields?.HeaderSubHeadline}
            HeaderCTA={props.fields?.HeaderCTA}
            headlineSize={props.params?.HeaderHeadlineSize}
        >
            {pageEditing && (
                <Flex flexDirection="column" gap="sp16" mb="sp16">
                    <Flex flexDirection="column">
                        Thumbnail URL:
                        <SCText
                            textField={props.fields?.ThumbnailUrl}
                            border="1px solid black"
                            mt="sp12"
                            wordBreak="break-word"
                        />
                    </Flex>
                    <Flex flexDirection="column">
                        Video URL:
                        <SCText
                            textField={props.fields?.VideoUrl}
                            border="1px solid black"
                            mt="sp12"
                            wordBreak="break-word"
                        />
                    </Flex>
                </Flex>
            )}
            <Box position="relative" data-group>
                <AspectRatio
                    maxW="100%"
                    ratio={16 / 9}
                    position="relative"
                    borderRadius={4}
                    overflow="hidden"
                >
                    <>
                        {(!isVideoShown || !videoElement) && (
                            <Image
                                src={props.fields?.ThumbnailUrl.value}
                                alt={props.fields?.Title?.value}
                                width={1120}
                                height={1120 * (9 / 16)}
                                sizes={sizes}
                            />
                        )}
                        {isVideoShown && hasVideo && (
                            <video
                                ref={videoRef}
                                src={props.fields?.VideoUrl.value}
                                onLoadedMetadata={onVideoLoad}
                                onTimeUpdate={updateSliderDuration}
                                onEnded={videoEnded}
                            >
                                <track kind="captions" />
                            </video>
                        )}
                    </>
                </AspectRatio>
                <Flex
                    position="absolute"
                    top={0}
                    bottom={0}
                    right={0}
                    left={0}
                    justifyContent="center"
                    alignItems="center"
                    onClick={isVideoShown ? playPauseMedia : undefined}
                    sx={
                        isVideoShown
                            ? {
                                  opacity: [1, null, null, 0],
                                  cursor: "pointer",
                                  transition: "all 0.3s ease",

                                  _groupHover: {
                                      opacity: 1,
                                  },
                              }
                            : {
                                  opacity: 1,
                              }
                    }
                >
                    {isVideoShown && (
                        <>
                            <IconButton
                                aria-label={t("closeVideo")}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    closePlayer();
                                }}
                                variant="secondary"
                                p={0}
                                width={48}
                                height={48}
                                position="absolute"
                                top="sp8"
                                right="sp8"
                                icon={<BundleIcon name="Close" />}
                            />
                            <Flex
                                position="absolute"
                                bottom={8}
                                left={8}
                                right={8}
                                display={["none", null, null, "flex"]}
                            >
                                <IconButton
                                    aria-label={
                                        videoElement?.paused ? t("playVideo") : t("pauseVideo")
                                    }
                                    variant="secondary"
                                    p={0}
                                    width={48}
                                    height={48}
                                    borderTopRightRadius={0}
                                    borderBottomRightRadius={0}
                                    flex="0 0 auto"
                                    icon={
                                        videoElement?.paused ? (
                                            <BundleIcon name="PlayArrow" />
                                        ) : (
                                            <BundleIcon name="Pause" />
                                        )
                                    }
                                />
                                <RangeSlider
                                    defaultValue={[0, 100]}
                                    value={[sliderValue]}
                                    onChange={(value) => {
                                        handleSliderChange(value[0]);
                                    }}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                    }}
                                    onChangeEnd={(value) => {
                                        handleSliderChange(value[0]);
                                    }}
                                    position="relative"
                                    flex={1}
                                >
                                    <RangeSliderTrack
                                        height={48}
                                        bg={transparentize(0.5, "white")}
                                        borderTopRightRadius={4}
                                        borderBottomRightRadius={4}
                                        aria-label={t("seekVideo")}
                                    >
                                        <RangeSliderFilledTrack height={48} bg="black" />
                                    </RangeSliderTrack>
                                    <RangeSliderThumb index={0} />
                                </RangeSlider>
                            </Flex>
                        </>
                    )}
                    {!isVideoShown && hasVideo && (
                        <IconButton
                            aria-label={t("playVideo")}
                            onClick={() => {
                                setIsVideoShown(true);
                            }}
                            variant="secondary"
                            p={0}
                            width={48}
                            height={48}
                            borderRadius="100%"
                            icon={<BundleIcon name="PlayArrow" />}
                        />
                    )}
                </Flex>
            </Box>
            <Flex mt="sp48" flexDirection={["column", null, null, "row"]}>
                <Box flex={1}>
                    <SCHeading as="h3" size="sm" textField={props.fields?.Title} />
                </Box>
                <Box flex={1} mt={["sp16", null, null, 0]}>
                    <SCText textField={props.fields?.Text} />
                    <SCLinkButton
                        linkField={props.fields?.CTA}
                        variant="primary"
                        mt="sp32"
                        rightIcon={<BundleIcon name="ArrowForward" />}
                    />
                </Box>
            </Flex>
        </GenericHeader>
    );
};
export default Video;
